import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { BriefForm } from "@/components/brief/BriefForm";
import { BriefSuccess } from "@/components/brief/BriefSuccess";
import { useBriefSubmission } from "@/hooks/useBriefSubmission";
import { EASE } from "@/lib/motion";

export const BriefModal = ({ open, onClose }) => {
  const brief = useBriefSubmission();

  const close = useCallback(() => {
    if (brief.inFlight.current) return;
    onClose();
    window.setTimeout(brief.resetDone, 500);
  }, [brief.inFlight, brief.resetDone, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.__lenis?.start();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[65] flex items-start justify-center overflow-hidden p-3 sm:items-center sm:p-6">
          <motion.button
            type="button"
            aria-label="Close"
            onClick={() => {
              if (!brief.dirty || brief.done) close();
            }}
            data-testid="brief-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 bg-[#050505]/88 backdrop-blur-[6px]"
          />
          <motion.div
            data-testid="brief-modal"
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, scale: 0.965, y: 26 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.975, y: 16 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="relative flex max-h-[92vh] w-full max-w-[94vw] flex-col overflow-hidden border border-[#262626] bg-[#0d0d0d] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] sm:max-h-[90vh] sm:max-w-[640px]"
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#1f1f1f] px-5 py-4 sm:px-9 sm:py-6">
              <div>
                <h3 className="font-display text-2xl font-black uppercase leading-none tracking-[-0.03em] text-[#e5e5e5] sm:text-3xl">
                  LET&apos;S MAKE IT.
                </h3>
                <p className="mt-2.5 text-sm text-[#a3a3a3]">Tell me what you&apos;re working on.</p>
              </div>
              <button
                type="button"
                onClick={close}
                data-testid="brief-close"
                aria-label="Close"
                className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#262626] text-[#a3a3a3] transition-colors duration-300 hover:border-[#d4ff00] hover:text-[#d4ff00]"
              >
                <X size={16} />
              </button>
            </div>
            {brief.done ? (
              <BriefSuccess onClose={close} />
            ) : (
              <BriefForm
                form={brief.form}
                busy={brief.busy}
                error={brief.error}
                setField={brief.setField}
                onSubmit={brief.submit}
              />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};